/**
 * TriangulationDrawer — PRISM ground-truth dropdown for Strategic Scenarios cards.
 *
 * Renders under each scenario (Ransomware / Data Breach / OT-ICS) in CompassModule.
 * Fetches triangulation data from /api/triangulation/scenarios/:scenarioKey?sessionId=N
 * and renders coverage / signal / named-gap rows per ATT&CK technique.
 *
 * Cross-product purple accent (#a855f7) matches the Phase 5 narrative convention
 * already used on Foundations Board Report.
 */

import React, { useEffect, useState, useCallback } from 'react';
import { ChevronDown, Shield, AlertCircle } from 'lucide-react';
import { authHeaders } from '../../../services/authHeaders';
import apiConfig from '../../../config/apiConfig';

export type ScenarioKey = 'ransomware' | 'breach' | 'otics';

interface TriangulationRow {
  technique_id: string;
  technique_name: string;
  required_classes: Array<{ code: string; name: string }>;
  matched_assets: Array<{ id: number; name: string; classes: string[] }>;
  coverage: 'covered' | 'partial' | 'gap';
  signal: 'signal' | 'partial' | 'none';
  named_gap: string | null;
  capability_evidence: Array<{
    capability_id: string;
    capability_name: string;
    mapping_strength: number;
  }>;
}

// Slice 1.5 — Strategic Actions ranked by leverage.
// Backend (PRISM attackScenarioTriangulationService.triangulateScenario) emits a
// pre-sorted array; rank=1 is the highest-leverage fix.
type LeverageTag = 'High' | 'Medium' | 'Low';

interface StrategicAction {
  rank: number;
  technique_id: string;
  technique_name: string;
  action: string;
  effort: string;
  leverage: LeverageTag;
  leverage_score: number;
  source_signal: string;
  /** Author-pinned highest-leverage flag from the backing gap_rule. When set,
   *  surfaces the "Highest-leverage fix" tag regardless of rank position
   *  (though backend sort pins these to rank=1, so they normally coincide). */
  is_highest_leverage?: boolean;
}

interface TriangulationResponse {
  success: boolean;
  connected: boolean;
  scenario_key: ScenarioKey;
  project_id?: number;
  project_name?: string;
  total_assets?: number;
  rows?: TriangulationRow[];
  summary?: {
    covered: number;
    partial_coverage: number;
    gap_coverage: number;
    signal: number;
    partial_signal: number;
    no_signal: number;
    named_gaps: number;
  };
  strategic_actions?: StrategicAction[];
  message?: string;
}

interface Props {
  sessionId: string | number | null;
  scenarioKey: ScenarioKey;
  scenarioLabel?: string;
}

const PURPLE = '#a855f7';
const PURPLE_SOFT = '#c4b5fd';
const PURPLE_BG = 'rgba(168, 85, 247, 0.08)';
const PURPLE_BORDER = 'rgba(168, 85, 247, 0.35)';

const STATUS_STYLES = {
  ok:      { color: '#10b981', bg: 'rgba(16, 185, 129, 0.12)',  border: 'rgba(16, 185, 129, 0.4)' },
  partial: { color: '#f59e0b', bg: 'rgba(245, 158, 11, 0.12)',  border: 'rgba(245, 158, 11, 0.45)' },
  gap:     { color: '#ef4444', bg: 'rgba(239, 68, 68, 0.12)',   border: 'rgba(239, 68, 68, 0.45)' },
};

function statusOf(c: TriangulationRow['coverage'] | TriangulationRow['signal']) {
  if (c === 'covered' || c === 'signal') return STATUS_STYLES.ok;
  if (c === 'partial') return STATUS_STYLES.partial;
  return STATUS_STYLES.gap;
}

function statusLabel(c: TriangulationRow['coverage'] | TriangulationRow['signal'], type: 'coverage' | 'signal') {
  if (type === 'coverage') {
    return c === 'covered' ? '● COVERED' : c === 'partial' ? '⚠ PARTIAL' : '● GAP';
  }
  return c === 'signal' ? '● SIGNAL' : c === 'partial' ? '⚠ PARTIAL' : '● NO SIGNAL';
}

const TriangulationDrawer: React.FC<Props> = ({ sessionId, scenarioKey, scenarioLabel }) => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<TriangulationResponse | null>(null);

  const fetchData = useCallback(async () => {
    if (!sessionId) return;
    setLoading(true);
    setError(null);
    try {
      // apiConfig, not a relative '/api/...': relative only works behind nginx, and in local dev it
      // returned the Vite index.html, which failed to parse on every attempt.
      const url = `${apiConfig.getApiBaseUrl()}/triangulation/scenarios/${scenarioKey}?sessionId=${sessionId}`;
      const res = await fetch(url, { headers: authHeaders() });
      const json: TriangulationResponse = await res.json();
      if (!res.ok || !json.success) {
        throw new Error(json.message || `HTTP ${res.status}`);
      }
      setData(json);
    } catch (err: any) {
      setError(err.message || 'Triangulation fetch failed');
    } finally {
      setLoading(false);
    }
  }, [sessionId, scenarioKey]);

  // Fetch lazily on first expand. `!error` is load-bearing: without it a failed fetch (401, 404, 500,
  // unparseable body) left data empty and loading false, so this effect re-fired immediately and
  // looped — over 1,000 requests in 5 seconds, measured 2026-09-16. Re-opening the drawer retries.
  useEffect(() => {
    if (open && !data && !loading && !error) {
      void fetchData();
    }
  }, [open, data, loading, error, fetchData]);

  const toggle = () => setOpen((o) => {
    if (!o) setError(null);
    return !o;
  });

  // Summary counts for the collapsed-state badge
  const summary = data?.summary;
  const connected = data?.connected ?? null;

  return (
    <div style={{ marginTop: 16 }}>
      <button
        type="button"
        onClick={toggle}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          width: '100%',
          padding: '14px 18px',
          background: PURPLE_BG,
          border: `1px solid ${PURPLE_BORDER}`,
          borderRadius: 8,
          color: PURPLE_SOFT,
          fontSize: 14,
          fontWeight: 600,
          cursor: 'pointer',
          textAlign: 'left',
          fontFamily: 'inherit',
        }}
        aria-expanded={open}
        aria-controls={`triangulation-body-${scenarioKey}`}
      >
        <span style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span
            style={{
              background: 'rgba(168, 85, 247, 0.25)',
              border: `1px solid ${PURPLE}`,
              color: PURPLE_SOFT,
              fontSize: 10,
              textTransform: 'uppercase',
              letterSpacing: '0.06em',
              fontWeight: 700,
              padding: '2px 8px',
              borderRadius: 3,
            }}
          >
            ◆ PRISM Ground-Truth
          </span>
          <span>Technical Triangulation — Coverage, Signal, Missing Controls</span>
        </span>
        <span style={{ display: 'flex', alignItems: 'center', gap: 14, fontSize: 12, fontWeight: 500, color: '#8892b0' }}>
          {summary && (
            <>
              <span><span style={{ color: STATUS_STYLES.ok.color }}>●</span>&nbsp;{summary.covered}/{(data?.rows?.length ?? 0)} covered</span>
              <span><span style={{ color: STATUS_STYLES.partial.color }}>⚠</span>&nbsp;{summary.partial_signal + summary.no_signal}/{(data?.rows?.length ?? 0)} signal gap</span>
              <span><span style={{ color: STATUS_STYLES.gap.color }}>●</span>&nbsp;{summary.named_gaps} named</span>
            </>
          )}
          {connected === false && <span style={{ color: '#8892b0' }}>No PRISM link</span>}
          <ChevronDown
            size={18}
            style={{ transform: open ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s ease' }}
          />
        </span>
      </button>

      {open && (
        <div
          id={`triangulation-body-${scenarioKey}`}
          style={{
            marginTop: 14,
            padding: 22,
            background: 'rgba(168, 85, 247, 0.05)',
            border: `1px solid rgba(168, 85, 247, 0.25)`,
            borderRadius: 8,
          }}
        >
          {/* Three-layer legend */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10, marginBottom: 18 }}>
            <LayerPill color="#ef4444" label="▲ THREAT" desc="ATT&CK chain from PRISM Attack Scenario" />
            <LayerPill color="#4fd1c5" label="◆ DEFENSE" desc="PRISM asset catalog × capability_to_attack" />
            <LayerPill color={PURPLE}  label="● IMPACT" desc="Foundations CRS + Recovery Cross-Check" />
          </div>

          {loading && (
            <div style={{ padding: 28, textAlign: 'center', color: '#8892b0', fontSize: 13 }}>
              Loading triangulation data from PRISM…
            </div>
          )}

          {error && (
            <div
              style={{
                padding: 14,
                background: 'rgba(239, 68, 68, 0.08)',
                border: '1px solid rgba(239, 68, 68, 0.3)',
                borderRadius: 6,
                color: '#fca5a5',
                fontSize: 13,
                display: 'flex',
                alignItems: 'center',
                gap: 10,
              }}
            >
              <AlertCircle size={16} />
              <span>Could not fetch triangulation: {error}</span>
            </div>
          )}

          {!loading && !error && data && connected === false && (
            <div
              style={{
                padding: 24,
                textAlign: 'center',
                color: '#ccd6f6',
                fontSize: 14,
                background: 'rgba(255,255,255,0.03)',
                borderRadius: 6,
              }}
            >
              <Shield size={28} style={{ color: PURPLE_SOFT, marginBottom: 10 }} />
              <div style={{ fontWeight: 600, marginBottom: 6 }}>No PRISM project linked to this assessment</div>
              <div style={{ fontSize: 12, color: '#8892b0' }}>
                Once your team's PRISM project is linked, this scenario will show coverage, signal,
                and missing-control analysis against your actual deployed defenses.
              </div>
            </div>
          )}

          {!loading && !error && data && connected === true && data.rows && (
            <>
              <div style={{ fontSize: 12, color: '#8892b0', marginBottom: 12 }}>
                Triangulated against PRISM project{' '}
                <strong style={{ color: '#e6f1ff' }}>{data.project_name}</strong>{' '}
                ({data.total_assets} assets)
              </div>

              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12.5 }}>
                  <thead>
                    <tr>
                      <Th>Technique</Th>
                      <Th>PRISM Assets Defending It</Th>
                      <Th style={{ width: 100 }}>Coverage</Th>
                      <Th style={{ width: 100 }}>Signal</Th>
                      <Th
                        style={{ width: 100 }}
                        title="Whether the deployed asset is actually configured for the protection — orthogonal to asset-presence Coverage. Derived from named-gap rules in PRISM's gap_rules dictionary."
                      >
                        Config
                      </Th>
                      <Th>Missing Control / Config</Th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.rows.map((row) => (
                      <Row key={row.technique_id} row={row} />
                    ))}
                  </tbody>
                </table>
              </div>

              {data.strategic_actions && data.strategic_actions.length > 0 && (
                <StrategicActionsStrip actions={data.strategic_actions} />
              )}

              <div
                style={{
                  marginTop: 14,
                  paddingTop: 10,
                  borderTop: '1px solid rgba(255,255,255,0.06)',
                  fontSize: 10,
                  color: '#6c7384',
                  fontFamily: '"SF Mono", Menlo, monospace',
                }}
              >
                source: PRISM dodcar.assets × capability_to_attack · scenario key:{' '}
                {scenarioLabel || scenarioKey}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

const LayerPill: React.FC<{ color: string; label: string; desc: string }> = ({ color, label, desc }) => (
  <div
    style={{
      background: 'rgba(28, 36, 52, 0.45)',
      border: '1px solid rgba(120, 140, 180, 0.20)',
      borderLeft: `3px solid ${color}`,
      borderRadius: 6,
      padding: '8px 12px',
      fontSize: 11,
    }}
  >
    <div style={{ color, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: 2, fontSize: 10 }}>
      {label}
    </div>
    <div style={{ color: '#ccd6f6' }}>{desc}</div>
  </div>
);

const Th: React.FC<{ children: React.ReactNode; style?: React.CSSProperties; title?: string }> = ({
  children,
  style,
  title,
}) => (
  <th
    title={title}
    style={{
      textAlign: 'left',
      padding: '8px 10px',
      color: '#8892b0',
      fontWeight: 600,
      fontSize: 10,
      textTransform: 'uppercase',
      letterSpacing: '0.06em',
      borderBottom: '1px solid rgba(120, 140, 180, 0.20)',
      ...style,
    }}
  >
    {children}
  </th>
);

// Slice 1.5.2: Config-validated status is derivable from named_gap. When the
// backing gap_rule fires (named_gap !== null), the deployed asset has a
// named configuration gap — orthogonal to "are the right asset classes
// present" (Coverage) and "do detective classes exist" (Signal). Surfaces
// the implementation-depth axis explicitly so COVERED+SIGNAL rows with a
// NAMED gap stop reading as visual contradiction.
//
// Returns 'validated' (no gap fired), 'unvalidated' (gap fired), or 'na'
// (no defense rule defined for the technique — empty required_classes).
function configValidatedStatus(row: TriangulationRow): 'validated' | 'unvalidated' | 'na' {
  if (row.required_classes.length === 0) return 'na';
  return row.named_gap === null ? 'validated' : 'unvalidated';
}

function configValidatedLabel(s: 'validated' | 'unvalidated' | 'na'): string {
  return s === 'validated' ? '● VALIDATED' : s === 'unvalidated' ? '⚠ UNVERIFIED' : '— N/A';
}

function configValidatedStyle(s: 'validated' | 'unvalidated' | 'na') {
  if (s === 'validated') return STATUS_STYLES.ok;
  if (s === 'unvalidated') return STATUS_STYLES.partial;
  // 'na' — muted, no border tint
  return { color: '#6c7384', bg: 'rgba(108, 115, 132, 0.10)', border: 'rgba(108, 115, 132, 0.30)' };
}

const Row: React.FC<{ row: TriangulationRow }> = ({ row }) => {
  const cov = statusOf(row.coverage);
  const sig = statusOf(row.signal);
  const cfg = configValidatedStatus(row);
  return (
    <tr>
      <td style={tdStyle}>
        <div style={{ fontFamily: '"SF Mono", Menlo, monospace', fontSize: 10, color: '#4fd1c5', fontWeight: 700 }}>
          {row.technique_id}
        </div>
        <div style={{ fontSize: 11, color: '#e6f1ff', marginTop: 2 }}>{row.technique_name}</div>
      </td>
      <td style={tdStyle}>
        {row.matched_assets.length === 0 && (
          <span style={{ fontSize: 11, color: '#8892b0', fontStyle: 'italic' }}>None matched</span>
        )}
        {row.matched_assets.map((a) => (
          <span
            key={a.id}
            title={a.classes.join(', ')}
            style={{
              display: 'inline-block',
              background: 'rgba(125, 211, 252, 0.10)',
              border: '1px solid rgba(125, 211, 252, 0.30)',
              color: '#7dd3fc',
              fontSize: 10,
              padding: '1px 6px',
              borderRadius: 3,
              fontWeight: 500,
              margin: '1px 2px 1px 0',
            }}
          >
            {a.name}
          </span>
        ))}
      </td>
      <td style={tdStyle}>
        <StatusBadge style={cov} label={statusLabel(row.coverage, 'coverage')} />
      </td>
      <td style={tdStyle}>
        <StatusBadge style={sig} label={statusLabel(row.signal, 'signal')} />
      </td>
      <td style={tdStyle}>
        <StatusBadge style={configValidatedStyle(cfg)} label={configValidatedLabel(cfg)} />
      </td>
      <td style={tdStyle}>
        {row.named_gap ? (
          <div style={{ fontSize: 11, color: '#ccd6f6', borderLeft: '2px solid #ef4444', paddingLeft: 6, lineHeight: 1.45 }}>
            <strong style={{ color: '#fca5a5' }}>NAMED: </strong>
            {row.named_gap}
          </div>
        ) : (
          <span style={{ fontSize: 11, color: '#8892b0' }}>None named</span>
        )}
      </td>
    </tr>
  );
};

const StatusBadge: React.FC<{
  style: { color: string; bg: string; border: string };
  label: string;
}> = ({ style, label }) => (
  <span
    style={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: 4,
      padding: '2px 7px',
      borderRadius: 3,
      fontSize: 10,
      fontWeight: 700,
      whiteSpace: 'nowrap',
      color: style.color,
      background: style.bg,
      border: `1px solid ${style.border}`,
    }}
  >
    {label}
  </span>
);

const tdStyle: React.CSSProperties = {
  padding: '12px 10px',
  borderBottom: '1px solid rgba(255,255,255,0.04)',
  verticalAlign: 'top',
};

// Slice 1.5 — Strategic Actions strip. Renders the ranked, leverage-scored
// actions derived from named-gap rules + capability mapping strength on the
// PRISM backend. Top action is tagged "Highest-leverage fix."
const StrategicActionsStrip: React.FC<{ actions: StrategicAction[] }> = ({ actions }) => (
  <div
    style={{
      marginTop: 16,
      padding: 14,
      background: 'rgba(168, 85, 247, 0.07)',
      borderRadius: 6,
      border: `1px dashed ${PURPLE_BORDER}`,
    }}
  >
    <h5
      style={{
        margin: '0 0 10px',
        fontSize: 11,
        color: PURPLE_SOFT,
        textTransform: 'uppercase',
        letterSpacing: '0.06em',
        fontWeight: 700,
      }}
    >
      ◆ Strategic Actions — ranked by leverage
    </h5>
    {actions.map((a, i) => (
      <div
        key={`${a.technique_id}-${a.rank}`}
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: 10,
          padding: '8px 0',
          borderTop: i === 0 ? 'none' : '1px solid rgba(255,255,255,0.04)',
          fontSize: 12,
        }}
      >
        <span style={{ color: PURPLE_SOFT, fontWeight: 700, minWidth: 18 }}>{a.rank}.</span>
        <span style={{ flex: 1, color: '#ccd6f6', lineHeight: 1.5 }}>
          <strong style={{ color: '#e6f1ff' }}>{a.action}</strong>
          {' — '}
          <span style={{ color: '#8892b0' }}>
            {a.source_signal}
          </span>
          {/* Tag follows the author-pinned flag, NOT rank position. Old PRISM
              versions that don't send is_highest_leverage fall back to rank===1
              so existing customers don't lose the tag during the rolling deploy. */}
          {(a.is_highest_leverage || (a.is_highest_leverage === undefined && a.rank === 1)) && (
            <em style={{ color: '#fca5a5', marginLeft: 6, fontStyle: 'italic', fontSize: 11 }}>
              Highest-leverage fix.
            </em>
          )}
        </span>
        <span
          style={{
            color: '#8892b0',
            fontSize: 11,
            whiteSpace: 'nowrap',
            paddingTop: 1,
          }}
        >
          {a.effort}
        </span>
      </div>
    ))}
  </div>
);

export default TriangulationDrawer;
